import { SecurityInventoryCategory, SecurityInventoryItem, Overlay } from '@x-guard/xgac-types/xgac';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useEffect, useMemo, useState } from 'react';
import { Identifier, useTranslate } from 'react-admin';
import { useTheme } from '@mui/material';
import { useGetList } from 'ra-core';
import {
  MapContainer, ScaleControl, useMap,
} from 'react-leaflet';
import { ItemMapMarker } from './ItemMapMarker';
import { CenterControl, CustomLayerControl, FloorFilter } from '../../../components/LeafletControls';
import {
  hasItemPosition, fitMapToCoords, MapBoundsCoords, hasButtonPosition,
  getButtonFloor, buttonToLatLng, DEFAULT_ZOOM, NETHERLANDS_CENTER,
  itemToLatLng, overlayToLatLngs,
} from '../util';
import { useOverlay } from '../../../../../utils/useOverlay';

const FitBoundsToItems = ({ coords, trigger }: { coords: MapBoundsCoords; trigger: number }) => {

  const map = useMap();
  useEffect(() => {

    fitMapToCoords(map, coords);

  }, [map, coords, trigger]);
  return null;

};

const getItemFloor = (item: SecurityInventoryItem) => {

  return item.location.properties?.address?.floor || 0;

};

const getOverlayFloor = (overlay: Overlay) => overlay.level ?? 0;

export const InventoryItemsMap = (props: {
  onMapReady: (map: L.Map | null) => void;
  itemsLoading: boolean;
  items: SecurityInventoryItem[];
  categories: SecurityInventoryCategory[];
  categoriesLoading: boolean;
  focusedItemId: Identifier | null;
  hoveredItemId: Identifier | null;
  onItemFocus: (itemId: Identifier | null) => void;
}) => {

  const [map, setMap] = useState<L.Map | null>(null);

  const [selectedFloors, setSelectedFloors] = useState<number[]>([]);
  const [fitTrigger, setFitTrigger] = useState(0);

  const [moveLocked, setMoveLocked] = useState(true);

  const translate = useTranslate();

  const theme = useTheme();
  const { overlays, isLoading: overlaysLoading } = useOverlay(map, selectedFloors);

  const visibleItems = useMemo(() => {

    if (selectedFloors.length === 0) return props.items;
    return props.items.filter((item) => {

      const floor = getItemFloor(item);
      return floor !== null && selectedFloors.includes(floor);

    });

  }, [props.items, selectedFloors]);

  const visibleOverlays = useMemo(() => {

    if (selectedFloors.length === 0) return overlays;
    return overlays.filter((overlay) => selectedFloors.includes(getOverlayFloor(overlay)));

  }, [overlays, selectedFloors]);

  const itemCoords = useMemo(
    () => visibleItems.filter(hasItemPosition).map(itemToLatLng),
    [visibleItems],
  );

  const overlayCoords = useMemo(
    () => visibleOverlays.flatMap(overlayToLatLngs),
    [visibleOverlays],
  );

  const needsButtons = !props.itemsLoading
    && !props.categoriesLoading
    && !overlaysLoading
    && itemCoords.length === 0
    && overlayCoords.length === 0;

  const { data: buttonsData = [], isLoading: buttonsLoading } = useGetList(
    'specials/bhv-knop/device-list',
    {
      pagination: { page: 1, perPage: -1 },
      sort: { field: 'name', order: 'ASC' },
      filter: {},
    },
    { enabled: needsButtons },
  );

  const buttonCoords = useMemo(
    () => buttonsData.filter(hasButtonPosition).map(buttonToLatLng),
    [buttonsData],
  );

  const boundsCoords = useMemo((): MapBoundsCoords => {

    if (props.itemsLoading || props.categoriesLoading) return undefined;
    if (itemCoords.length > 0) return itemCoords;
    if (overlaysLoading) return undefined;
    if (overlayCoords.length > 0) return overlayCoords;
    if (needsButtons && buttonsLoading) return undefined;
    if (buttonCoords.length > 0) return buttonCoords;
    return null;

  }, [
    props.itemsLoading,
    props.categoriesLoading,
    overlaysLoading,
    itemCoords,
    overlayCoords,
    buttonCoords,
    needsButtons,
    buttonsLoading,
  ]);

  const possibleFloors = useMemo(() => {

    const floors = [
      ...props.items.map(getItemFloor),
      ...overlays.map(getOverlayFloor),
    ];
    if (needsButtons || buttonsData.length > 0) {

      floors.push(...buttonsData.map(getButtonFloor));

    }
    return Array.from(new Set(floors)).sort((a, b) => a - b);

  }, [props.items, overlays, buttonsData, needsButtons]);

  useEffect(() => {

    setFitTrigger((x) => x + 1);

  }, [selectedFloors]);

  return (
    <MapContainer
      ref={(ref: L.Map | null) => {

        setMap(ref);
        props.onMapReady(ref);

      }}
      center={NETHERLANDS_CENTER}
      zoom={DEFAULT_ZOOM}
      id="inventory-items-map-container"
      minZoom={4}
      zoomControl={false}
      style={{ height: '100%', width: '100%' }}
    >
      <div>
        <div className="leaflet-control-container-custom">
          <CustomLayerControl map={map} toggleVisible={true}/>
          <CenterControl onClick={() => {

            setFitTrigger((x) => x + 1);

          }}/>
          <button
            onClick={() => setMoveLocked((v) => !v)}
            className="map-control-button"
            style={moveLocked ? {
              backgroundColor: theme.palette.error.main,
              color: 'white',
            } : {
              backgroundColor: theme.palette.primary.main,
              color: 'white',
            }}
          >
            {moveLocked ? <LockIcon /> : <LockOpenIcon />}
            <span>{translate(moveLocked ? 'resources.buttons.text.locked' : 'resources.buttons.text.unlocked')}</span>
          </button>
        </div>
        <div className="second-filter-div" style={{
          left: '260px',
        }}>
          <FloorFilter floors={possibleFloors} selectedFloors={selectedFloors} onChange={setSelectedFloors}/>
        </div>
      </div>

      <ScaleControl position="bottomright" imperial={false} />

      <FitBoundsToItems coords={boundsCoords} trigger={fitTrigger} />

      {visibleItems.filter(hasItemPosition).map((item) => (
        <ItemMapMarker
          key={item._id}
          item={item}
          categories={props.categories}
          focusedItemId={props.focusedItemId}
          onFocus={props.onItemFocus}
          hoveredItemId={props.hoveredItemId}
          moveLocked={moveLocked}
        />
      ))}
    </MapContainer>
  );

};
